import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'app/store';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { signupApi } from '../services/signupApi';

/**
 * Component that displays when an existing account is detected during signup.
 * Shows the user's progress and provides a "Sign In to Continue" button.
 */
const AccountFoundPage: React.FC = () => {
  const { accountProgress } = useSelector((state: RootState) => state.signup);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  if (accountProgress == null) {
    return null;
  }

  /**
   * Handler for sign in button click - redirects to Auth0 login
   */
  const handleSignIn = (): void => {
    // Redirect to the Auth0 login endpoint
    window.location.href = '/onboarding';
  };

  /**
   * Handler for password reset button click
   * Generates Auth0 password reset URL and redirects user
   */
  const handlePasswordReset = async (): Promise<void> => {
    setIsResettingPassword(true);
    try {
      const response = await signupApi.requestPasswordReset(accountProgress.email);
      // Redirect to Auth0 password reset URL
      window.location.href = response.reset_url;
    } catch (error) {
      // On error, fallback to /onboarding where user can use forgot password
      setIsResettingPassword(false);
      window.location.href = '/onboarding';
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome back header */}
      <div className="text-center">
        <h3 className="text-2xl text-gray-900">Welcome back!</h3>
        <p className="mt-2 text-sm text-gray-600">We found your account: {accountProgress.email}</p>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>{accountProgress.friendly_milestone}</span>
          <span>{accountProgress.progress_percentage}% complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-dataops-primary-blue h-3 rounded-full transition-all duration-300"
            style={{ width: `${accountProgress.progress_percentage}%` }}
          ></div>
        </div>
      </div>

      {/* Status checklist */}
      <div className="border border-gray-200 rounded-md p-4 space-y-3">
        <h4 className="text-sm text-gray-700">Setup Progress:</h4>

        <StatusCheckpoint label="Account created" completed={true} />
        <StatusCheckpoint label="Snowflake connected" completed={accountProgress.has_snowflake_account} />
        <StatusCheckpoint label="DataOps runner deployed" completed={accountProgress.has_runner} />
      </div>

      {/* Helper text */}
      <p className="text-sm text-gray-600 text-center">
        Sign in to continue your setup and complete the remaining steps.
      </p>

      {/* Sign in button */}
      <button
        onClick={handleSignIn}
        type="button"
        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm text-white bg-dataops-primary-blue hover:bg-hover-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dataops-primary-blue transition-colors"
      >
        Sign In to Continue
      </button>

      {/* Forgot password link */}
      <div className="text-center">
        <button
          onClick={() => {
            void handlePasswordReset();
          }}
          disabled={isResettingPassword}
          type="button"
          className="text-sm text-dataops-secondary-blue hover:text-hover-secondary-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isResettingPassword ? 'Generating reset link...' : 'Forgot your password?'}
        </button>
      </div>
    </div>
  );
};

/**
 * Component that displays a single checkpoint in the status list.
 */
const StatusCheckpoint: React.FC<{ label: string; completed: boolean }> = ({ label, completed }) => {
  return (
    <div className="flex items-center space-x-3">
      {completed ? (
        <CheckCircleIcon className="h-6 w-6 text-green-highlight" aria-hidden="true" />
      ) : (
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 bg-white" aria-hidden="true"></div>
      )}
      <span className={`text-sm ${completed ? 'text-gray-900' : 'text-gray-500'}`}>{label}</span>
    </div>
  );
};

export default AccountFoundPage;
